import React, {useEffect, useState} from 'react'
import {Trans, useTranslation} from 'react-i18next'
import moment from 'moment'
import classNames from 'classnames'
import {Typography, Button, Divider} from 'antd'
import DatePicker from '~/code/components/dumb/DatePicker/DatePicker'
import {observer} from 'mobx-react'
import {SERVER_DATE_FORMAT} from '@/constants'
import {Modal} from '@/components'
import {DownloadOutlined} from '@ant-design/icons'
import {useScreenType} from '@/hooks'
import {disabledDateForPdf, getLastFullMonth} from '../../utils'
import {isOctoberRestrictionEnabled} from '@/constants/dateRangePresets'
import {disabledDatesBeforeOctober2025} from '@/utils/moment-utils'
import {CANCEL_BUTTON, DOWNLOAD_BUTTON} from '@/constants/playwright-ids'

import {SettlementsPdfDownloadModalProps} from './props'
import styles from './styles.scss'

const DATE_NEW = moment({year: 2023, month: 7}) // 2023-08
const DATE_PERIOD_FORMAT = 'YYYY-MM'

export const SettlementsPdfDownloadModal = observer((props: SettlementsPdfDownloadModalProps) => {
    const {t} = useTranslation()
    const {isOpen, onCancel, onSubmit} = props
    const {isMobile} = useScreenType()

    const [date, setDate] = useState(getLastFullMonth())
    const isOld = date.isBefore(DATE_NEW)

    const combinedDisabledDate = (date: moment.Moment) => {
        if (isOctoberRestrictionEnabled && disabledDatesBeforeOctober2025(date)) {
            return true
        }
        return disabledDateForPdf(date)
    }

    const findFirstAllowedMonth = (value: moment.Moment): moment.Moment | null => {
        const candidate = value.clone().startOf('year')
        for (let month = 0; month < 12; month++) {
            candidate.month(month)
            if (!combinedDisabledDate(candidate)) {
                return candidate
            }
        }
        return null
    }

    const disabledYear = (date: moment.Moment) => !findFirstAllowedMonth(date)

    const changeDate = (value: moment.Moment) => {
        if (combinedDisabledDate(value)) {
            setDate(findFirstAllowedMonth(value) || getLastFullMonth())
        } else {
            setDate(value)
        }
    }

    const download = () => {
        onSubmit(
            isOld
                ? {
                      from: moment(date).startOf('month').format(SERVER_DATE_FORMAT),
                      to: moment(date).endOf('month').format(SERVER_DATE_FORMAT)
                  }
                : {period: date.format(DATE_PERIOD_FORMAT)}
        )
    }

    useEffect(() => {
        if (isOpen) {
            setDate(getLastFullMonth())
        }
    }, [isOpen, setDate])

    return (
        <Modal
            title={t('SettlementsPdfDownloadModal.title')}
            width={'580px'}
            open={isOpen}
            footer={null}
            onCancel={onCancel}
        >
            <Typography.Text>
                <Trans
                    i18nKey='SettlementsPdfDownloadModal.text'
                    components={{strong: <strong />, br: <br />}}
                />
            </Typography.Text>

            <div className={classNames(styles.datepickers, {[styles.isMobile]: isMobile})}>
                <DatePicker
                    value={date}
                    picker='year'
                    disabledDate={disabledYear}
                    className={styles.yearDatePicker}
                    onChange={changeDate}
                    getPopupContainer={(triggerNode) => triggerNode.parentNode as HTMLElement}
                />
                <DatePicker
                    value={date}
                    format='MMMM'
                    picker='month'
                    disabledDate={combinedDisabledDate}
                    className={styles.monthDatePicker}
                    popupClassName={styles.monthDatePickerPopup}
                    onChange={changeDate}
                    getPopupContainer={(triggerNode) => triggerNode.parentNode as HTMLElement}
                />
            </div>

            <Divider />

            <div className={styles.buttons}>
                <Button
                    type='primary'
                    onClick={download}
                    icon={<DownloadOutlined />}
                    data-test-id={DOWNLOAD_BUTTON}
                >
                    {t('SettlementsPdfDownloadModal.download')}
                </Button>
                <Button onClick={onCancel} data-test-id={CANCEL_BUTTON}>
                    {' '}
                    {t('SettlementsPdfDownloadModal.cancel')}
                </Button>
            </div>
        </Modal>
    )
})
